/**
 * Search service using MiniSearch
 * Provides full-text search across object data with configurable field weights
 */

import MiniSearch from 'minisearch';
import {
	ALL_SEARCHABLE_FIELD_NAMES,
	FIELD_WEIGHTS,
	FUZZY_TOLERANCE,
	SEARCH_COMBINE_MODE,
	type SearchableFieldName,
} from '../config/searchConfig';
import { OBJECT_FIELDS } from '../constants/objectFields';
import type { ObjectData } from '../types/metadata';

/**
 * Search result with scoring information
 */
export interface SearchResult {
	/** Object identifier */
	readonly id: string;
	/** Search relevance score */
	readonly score: number;
	/** Matching terms per searchable field */
	readonly match: Partial<Record<SearchableFieldName, string[]>>;
}

/**
 * Search options
 */
export interface SearchOptions {
	/** Which searchable fields to include (defaults to all) */
	readonly activeFields?: SearchableFieldName[];
	/** Enable fuzzy matching (defaults to true) */
	readonly fuzzy?: boolean | number;
	/** Enable prefix matching (defaults to true) */
	readonly prefix?: boolean;
}

/**
 * Search service class that manages MiniSearch index
 */
export class SearchService {
	private miniSearch: MiniSearch<ObjectData> | null = null;

	private indexedObjects: ObjectData[] = [];

	/**
	 * Initialize and build the search index from objects
	 */
	buildIndex(objects: ObjectData[]): void {
		// Store objects for later reference
		this.indexedObjects = objects;

		// Initialize MiniSearch with dynamic fields
		this.miniSearch = new MiniSearch({
			fields: ALL_SEARCHABLE_FIELD_NAMES, // Fields to index
			storeFields: [OBJECT_FIELDS.IDENTIFIER, OBJECT_FIELDS.TITLE], // Fields to return in results
			idField: OBJECT_FIELDS.IDENTIFIER, // Use object identifier as ID
			searchOptions: {
				boost: FIELD_WEIGHTS, // Apply field weights
				fuzzy: FUZZY_TOLERANCE, // Fuzzy matching tolerance (0-1)
				prefix: true, // Enable prefix matching (partial words)
			},
		});

		// Add all objects to the index
		this.miniSearch.addAll(objects);
	}

	/**
	 * Search the index with optional field filtering
	 */
	search(query: string, options: SearchOptions = {}): SearchResult[] {
		// Early validation - ensure index is built before searching
		if (!this.miniSearch) {
			throw new Error(
				'Search index not initialized. Call buildIndex() first or check that indexReady is true before searching.',
			);
		}

		// Default options - use all fields and standard fuzzy/prefix settings
		const { activeFields = ALL_SEARCHABLE_FIELD_NAMES, fuzzy = FUZZY_TOLERANCE, prefix = true } = options;

		// Validate options upfront rather than silently accepting invalid values
		if (typeof fuzzy === 'number' && (fuzzy < 0 || fuzzy > 1)) {
			throw new Error(`Fuzzy tolerance must be between 0 and 1, got ${fuzzy}`);
		}
		if (typeof prefix !== 'boolean') {
			throw new Error(`Prefix must be a boolean, got ${typeof prefix}`);
		}

		// Build field boost weights (only for active fields - optimized for performance)
		// This creates a { fieldName: weight } object dynamically based on active fields
		// Example: { 'dcterms:title': 3, 'dcterms:description': 1 }
		const boost = Object.fromEntries(activeFields.map((field) => [field, FIELD_WEIGHTS[field]]));

		// Perform search with custom options
		const results = this.miniSearch.search(query, {
			fields: activeFields, // Only search in active fields (narrower search = better performance)
			boost, // Apply field weights (title matches score higher than description matches)
			fuzzy, // Fuzzy matching tolerance (0.2 = allow ~1 character difference per 5 chars)
			prefix, // Prefix matching enabled (allows partial word matches like "ston" matching "stone")
			combineWith: SEARCH_COMBINE_MODE, // AND mode = all query terms must match (more precise results)
		});

		// Map to our SearchResult interface
		return results.map((result) => ({
			id: result.id,
			score: result.score,
			match: result.match,
		}));
	}

	/**
	 * Get object by ID from indexed objects
	 */
	getObjectById(id: string): ObjectData | undefined {
		return this.indexedObjects.find((obj) => obj[OBJECT_FIELDS.IDENTIFIER] === id);
	}

	/**
	 * Get total number of indexed objects
	 */
	getIndexSize(): number {
		return this.indexedObjects.length;
	}

	/**
	 * Check if index is ready
	 */
	isReady(): boolean {
		return this.miniSearch !== null;
	}

	/**
	 * Clear the index
	 */
	clear(): void {
		this.miniSearch = null;
		this.indexedObjects = [];
	}
}

// Export singleton instance
export const searchService = new SearchService();
